import { Controller } from "@hotwired/stimulus"

// registration step 系 view の sticky 「次へ」ボタン用 controller (#1833 PR 7)。
//
// 旧 _sticky_button_script.html.erb partial (53 行) の inline JS を集約。
// 3 つの責務を 1 controller で扱う:
// 1. button 分の padding-bottom を container / 最外 wrapper に追加 (button overlap 防止)
// 2. window resize で padding を再計算 (orientation 変更等)
// 3. form submit 時の二重送信防止 (button disable + "送信中..." 表示)
//
// scope: <div class="min-h-screen ..."> outer wrapper に置く (form / container / button
// すべてを subtree に含む)。controller / container / button targets で各要素を指定。
//
// XSS 安全性: textContent / style 数値 / class 名のみ、innerHTML 不使用。
//
// Rule of Three: registration 系 step view 専用のため style 数値は hardcoded (旧 inline
// JS と parity)。共通化は registration 以外でも sticky button が出てきた時に再考。
export default class extends Controller {
  static targets = ["container", "button"]

  connect() {
    // 旧 inline JS は `if (!button || !container) return` で graceful に no-op していた。
    // step view によっては _fixed_submit_button partial を render しない (例:
    // job_change_intention)。layout_lp 側で data-controller が attach されていても
    // button target 不在なら何もせず exit する pattern を維持。
    if (!this.hasButtonTarget || !this.hasContainerTarget) return

    this.handleResize = this.handleResize.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)

    this.adjustContainerPadding()
    window.addEventListener("resize", this.handleResize)

    // form submit 二重送信防止: 旧実装と同様に button が closest('form') を持つ前提
    this.form = this.buttonTarget.closest("form")
    if (this.form) {
      this.form.addEventListener("submit", this.handleSubmit)
    }
  }

  disconnect() {
    // connect() で early return した場合 handleResize 等は未 bind なので何もしない
    if (!this.handleResize) return

    window.removeEventListener("resize", this.handleResize)
    if (this.form) {
      this.form.removeEventListener("submit", this.handleSubmit)
      this.form = null
    }
    // resize debounce timer cleanup (controller がはずれた後に発火しないよう構造的に防ぐ)
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId)
      this.resizeTimeoutId = null
    }
  }

  adjustContainerPadding() {
    // iPhone X 以降のホームインジケーター対応。jsdom (test env) は CSS.supports の
    // 一部実装が不完全で throw する場合があるため try/catch で囲む。real browser は
    // throw しないので production には影響なし。
    try {
      if (CSS.supports("padding-bottom", "env(safe-area-inset-bottom)")) {
        const isMobile = window.innerWidth < 640
        const basePadding = isMobile ? 12 : 16
        this.buttonTarget.style.paddingBottom = `calc(${basePadding}px + env(safe-area-inset-bottom))`
      }
    } catch {
      // CSS.supports 不在 / 不完全 環境 (jsdom) は no-op で続行。
    }

    // safe area の余白を適用してから測り、初回とリサイズ後で同じ高さを使う。
    const buttonHeight = this.buttonTarget.offsetHeight
    this.containerTarget.style.paddingBottom = `${buttonHeight + 100}px`
    // 最外 wrapper (min-h-screen) にも padding を付与してフッターの可視性を確保
    this.element.style.paddingBottom = "100px"
  }

  handleResize() {
    // 100ms debounce (旧 inline JS parity)
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId)
    }
    this.resizeTimeoutId = setTimeout(() => {
      this.resizeTimeoutId = null
      this.adjustContainerPadding()
    }, 100)
  }

  handleSubmit() {
    const submitButton = this.form.querySelector('input[type="submit"]')
    if (submitButton && !submitButton.disabled) {
      submitButton.disabled = true
      submitButton.value = "送信中..."
    }
  }
}
